import { useState } from 'react';
import axiosInstance from 'interceptor/axiosInstance';
import { ApiRoutes } from 'routes/routeConstants/apiRoutes';
import { generatePath } from 'react-router-dom';
import { deserialize } from 'serializr';
import { Supplier } from 'models/Supplier/Supplier.model';
import { ValueChainServiceProps } from 'enums/ValueChainServiceEnums';
import { limitParam } from 'views/Scope3/Category4&9';
import Notification from 'shared/components/Notification';
import { NotificationTypes } from 'enums/NotificationEnums';
import { SupplierApiMessages } from 'shared/components/ValueChain/Suppliers/suppliersuccessmessage';

export interface SupplierCreationUpdation {
  name: string | undefined;
  company_id: number;
  address: {
    address_name: string | undefined;
    street: string | undefined;
    city: string | undefined;
    province: string | undefined;
    country: string | undefined;
    postal_code: string | undefined;
    latitude: string | undefined;
    longitude: string | undefined;
    country_code: string | undefined;
  };
}


const SupplierService = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [supplierList, setSupplierList] = useState<Supplier[]>([]);
  const [supplierResponse, setSupplierResponse] = useState<Supplier>();
  const [pageSize, setPageSize] = useState<number>(20);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);

  /* Fetch Brands list */
  const fetchSuppliers = async (params?: {
    page?: number;
    size?: number;
    name?: string;
  }) => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(ApiRoutes.FETCH_SUPPLIERS, {
        params,
      });
      const data = deserialize(
        Supplier,
        response.data[ValueChainServiceProps.DATA]
      ) as Supplier[];

      setSupplierList(data);
      setTotalPages(response.data[ValueChainServiceProps.TOTAL_PAGES]);
      setTotalItems(response.data[ValueChainServiceProps.TOTAL_ITEMS]);
      setCurrentPage(response.data[ValueChainServiceProps.CURRENT_PAGE]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /* Handle supplier delete */
  const deleteSupplier = async (id: number) => {
    try {
      setLoading(true);

      const url = generatePath(ApiRoutes.DELETE_SUPPLIER, { id });
      await axiosInstance.delete(url);
      const updatedArr = supplierList.filter(element => element.id !== id);
      setSupplierList(updatedArr);
      Notification({
        message: SupplierApiMessages.Supplier_DELETED,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** Create Supplier */
  const createSupplier = async (supplier: SupplierCreationUpdation) => {
    try {
      setLoading(true);
      const res = await axiosInstance.post(ApiRoutes.CREATE_SUPPLIER, supplier);
      const updatedArray = [
        ...supplierList,
        res.data.data.supplier as Supplier,
      ];
      setSupplierList(updatedArray);
      setSupplierResponse(res.data.data.supplier);
      fetchSuppliers(limitParam);
      return res.data.data.supplier;
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  /** Update Supplier */
  const updateSupplier = async (
    id: number | undefined,
    Supplier: SupplierCreationUpdation
  ) => {
    try {
      setButtonLoading(true);
      const url = generatePath(ApiRoutes.UPDATE_SUPPLIER, { id });
      await axiosInstance.put(url, Supplier);
      await fetchSuppliers({ size: 20 });
      Notification({
        message: SupplierApiMessages.Supplier_UPDATED,
        description: '',
        type: NotificationTypes.SUCCESS,
      });
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  return {
    supplierList,
    loading,
    pageSize,
    totalItems,
    totalPages,
    currentPage,
    buttonLoading,
    supplierResponse,
    fetchSuppliers,
    setPageSize,
    createSupplier,
    deleteSupplier,
    updateSupplier,
  };
};

export default SupplierService; 